import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { SectionHeading } from '../components/ui/SectionHeading';
import {
  Sparkles,
  ArrowRight,
  MousePointerClick,
  Globe2,
  Rocket,
  CheckCircle2,
  Clock,
  Laptop,
  Check,
} from 'lucide-react';

export const HowItWorksPage: React.FC = () => {
  const { navigate } = useNavigation();

  const steps = [
    {
      stepNumber: '01',
      title: 'Sektörünüzü ve Uygun Temayı Seçin',
      duration: '2 Dakika',
      description:
        'Kurumsal, lüks yat kiralama, hukuk veya sağlık gibi sektörlerden işletmenize en uygun olanı belirleyin. Uzmanlarımız tarafından sektörünüze özel tasarlanmış şablonlardan birini tek tıkla seçin.',
      bullets: [
        'Sektöre göre otomatik modül yapılandırması',
        'Hazır kurumsal sayfalar (Hakkımızda, Hizmetler, Filo, İletişim)',
        'Mobil uyumlu modern arayüzler',
      ],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=800&auto=format&fit=crop',
    },
    {
      stepNumber: '02',
      title: 'İçeriklerinizi ve Markanızı Ekleyin',
      duration: '5 Dakika',
      description:
        'Görsel editör ile logolarınızı yükleyin, kurumsal renklerinizi uygulayın. Metinleri doğrudan sayfada tıklayarak değiştirin ve şirketinizin fotoğraflarını galeriye ekleyin.',
      bullets: [
        'Sürükle-bırak canlı editör kolaylığı',
        'Yüksek çözünürlüklü otomatik görsel optimizasyonu',
        'Gerçek zamanlı masaüstü ve mobil önizleme',
      ],
      image: 'https://images.unsplash.com/photo-1542744094-3a31f272c490?q=80&w=800&auto=format&fit=crop',
    },
    {
      stepNumber: '03',
      title: 'Sektörel Modülleri Aktifleştirin',
      duration: '3 Dakika',
      description:
        'Yat kiralama sektöründeyseniz teknelerinizi, kabin ve uzunluk bilgilerini girin; kurumsal firmaysanız hizmet paketlerinizi ve iletişim formlarınızı yapılandırın.',
      bullets: [
        'Yat filosu yönetimi ve sezonluk fiyatlandırma',
        'Müşteri rezervasyon ve talep alma formu',
        'Doğrudan WhatsApp entegrasyonu',
      ],
      image: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?q=80&w=800&auto=format&fit=crop',
    },
    {
      stepNumber: '04',
      title: 'Domaininizi Bağlayın ve Yayına Alın',
      duration: '1 Dakika',
      description:
        'Kendi alan adınızı (.com veya .com.tr) girin. Sistemimiz DNS kayıtlarını doğrular, ücretsiz 256-bit SSL sertifikanızı oluşturur ve sitenizi global Edge CDN üzerinde anında yayına alır.',
      bullets: [
        'Otomatik SSL sertifikası ve HTTPS yönlendirmesi',
        'Google SEO optimizasyonu ve sitemap.xml',
        'Yıldırım hızında global barındırma',
      ],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=800&auto=format&fit=crop',
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <SectionHeading
          badge="Nasıl Çalışır?"
          title="Fikirden Yayına 4 Basit Adım"
          subtitle="Teknik bilgi veya yazılımcı ihtiyacı duymadan, profesyonel web sitenizi dakikalar içinde canlıya alın."
        />

        {/* Total Time Callout */}
        <div className="mt-8 flex items-center justify-center gap-2 text-xs font-semibold text-blue-400 bg-blue-500/10 border border-blue-500/20 py-2 px-4 rounded-full w-fit mx-auto">
          <Clock className="w-4 h-4" />
          <span>Ortalama Yayın Süresi: 10 - 15 Dakika</span>
        </div>

        {/* Steps List */}
        <div className="mt-16 space-y-16">
          {steps.map((step, idx) => {
            const isReversed = idx % 2 !== 0;

            return (
              <div
                key={idx}
                className="p-6 sm:p-10 rounded-3xl bg-slate-900/80 border border-slate-800 shadow-2xl"
              >
                <div
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
                    isReversed ? 'lg:flex-row-reverse' : ''
                  }`}
                >
                  {/* Left / Text column */}
                  <div
                    className={`lg:col-span-7 space-y-6 ${
                      isReversed ? 'lg:order-2' : 'lg:order-1'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-3xl sm:text-4xl font-extrabold font-mono text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                        {step.stepNumber}
                      </span>
                      <div className="h-6 w-[1px] bg-slate-800" />
                      <span className="text-xs font-semibold px-2.5 py-1 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {step.duration}
                      </span>
                    </div>

                    <h3 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
                      {step.title}
                    </h3>

                    <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                      {step.description}
                    </p>

                    <div className="space-y-2.5 pt-2">
                      {step.bullets.map((bullet, i) => (
                        <div key={i} className="flex items-start gap-3 text-xs sm:text-sm text-slate-200">
                          <div className="w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3" />
                          </div>
                          <span>{bullet}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right / Visual column */}
                  <div
                    className={`lg:col-span-5 ${
                      isReversed ? 'lg:order-1' : 'lg:order-2'
                    }`}
                  >
                    <div className="relative rounded-2xl overflow-hidden border border-slate-800 shadow-2xl aspect-[4/3]">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom Callout */}
        <div className="mt-20 p-8 sm:p-12 rounded-3xl bg-gradient-to-tr from-blue-900/30 to-slate-900 border border-blue-500/20 text-center max-w-3xl mx-auto">
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Kendi Web Sitenizi Oluşturmaya Hazır mısınız?
          </h3>
          <p className="text-slate-300 text-sm mb-6 max-w-xl mx-auto">
            14 gün boyunca hiçbir taahhütte bulunmadan tüm özellikleri ücretsiz deneyin.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/kayit')}
              className="px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm shadow-xl shadow-blue-600/30 flex items-center gap-2 transition"
            >
              <span>Ücretsiz Site Oluştur</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/temalar')}
              className="px-6 py-3.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 font-semibold text-sm border border-slate-700 transition"
            >
              Temaları İncele
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
